import reactUpdate from 'immutability-helper';
import {uniqueId} from 'underscore';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {updateStoichiometryCalculationSettingsActionFunction} from 'notebook/integrations/chemistry/StoichiometryCalculationSettingsModal';
import {
  downloadStoichiometryCSVActionFunction,
  exportStoichiometryAuditLogsActionFunction,
} from 'notebook/integrations/chemistry/stoichiometryGroupActionFunctions';
import {StoichiometryStructuredTable} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import submitReactionGroupActionFunction from 'notebook/notes/groups/action-functions/submitReactionGroupActionFunction';
import GenericNoteLinkedGroupAdapter from 'notebook/notes/groups/GenericNoteLinkedGroupAdapter';
import {
  CreateNoteLinkedGroupProps,
  GroupActionFunctions,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {STRUCTURED_TABLE_PLUGIN} from 'notebook/structured-tables/StructuredTablePlugin';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {isNoteLinkedFile, isStructuredTable} from 'notebook/TypePredicates';
import {
  NoteLinkedFile,
  NoteLinkedObjectType,
  NotePart,
  NotePartWithItem,
  isNotePartWithItem,
} from 'notebook/util/NoteUtilConstants';
import {Schema} from 'schemas/SchemaConstants';

import type {
  StoichiometryCreateGroupExtras,
  StoichiometryGroup,
  StoichiometryGroupMetadata,
} from './StoichiometryTypes';

/**
 * A default empty NoteLinkedFile used for rendering the reaction scheme sketcher
 * when no NoteLinkedFile part exists yet in the group (e.g., legacy groups or
 * newly created groups before a reaction scheme is drawn).
 */
export function makeEmptyReactionSchemeFile(name: string): NoteLinkedFile {
  return {
    cid: uniqueId('nlf'),
    type: NoteLinkedObjectType.NOTE_LINKED_FILE,
    name,
    tfiFileMeta: null,
    externalFileExcerpt: null,
    excerptType: null,
    externalFile: null,
    previewFile: null,
    sourcePreviewFile: null,
    missingPreviewWarning: null,
    isPreviewTransformed: null,
  };
}

export type {StoichiometryCreateGroupExtras, StoichiometryGroup, StoichiometryGroupMetadata};

export class StoichiometryNoteLinkedGroupAdapter extends GenericNoteLinkedGroupAdapter<
  StoichiometryGroupMetadata,
  StoichiometryCreateGroupExtras
> {
  getDisplayName(_group: StoichiometryGroup): string {
    return 'Stoichiometry';
  }

  override getActionFunctions(): GroupActionFunctions<
    StoichiometryGroupMetadata,
    StoichiometryCreateGroupExtras
  > {
    const inherited = super.getActionFunctions();
    return {
      ...inherited,
      primary: submitReactionGroupActionFunction,
      secondary: [updateStoichiometryCalculationSettingsActionFunction, ...(inherited.secondary ?? [])],
      overflow: [
        ...(inherited.overflow ?? []),
        exportStoichiometryAuditLogsActionFunction,
        downloadStoichiometryCSVActionFunction,
      ],
      footer: inherited.footer ?? [],
    };
  }

  createNoteParts(
    createProps: CreateNoteLinkedGroupProps<StoichiometryGroupMetadata, StoichiometryCreateGroupExtras>
  ): Array<Omit<NotePart, 'cid'>> {
    assert(
      createProps.kind === this.kind,
      `Stoichiometry adapter only supports ${this.kind}, got ${createProps.kind}`
    );
    const {name, entryId, selectorsById, isConfig, extras} = createProps;
    const stoichiometryTable = STRUCTURED_TABLE_PLUGIN.get(StructuredTableKind.STOICHIOMETRY).createTable({
      name,
      schemas: [],
      entryId,
      metadata: {},
      selectorsById,
      isConfig,
      structuredTableInsertionContext:
        extras.structuredTableInsertionContext ?? EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
    });

    // @ts-expect-error(2322): This is passing in `shouldGenerateCid: false` but we are expecting it to have a cid
    const tablePart: Omit<NotePart, 'cid'> = reactUpdate(
      makeDefaultItem(ModelTypeConstants.NOTE_LINKED_OBJECT, null, {shouldGenerateCid: false}),
      {
        // @ts-expect-error(2322): The NotePart StructuredTable mapping uses unknown instead of a type parameter
        item: {$set: stoichiometryTable},
      }
    );

    return [tablePart];
  }

  getReactionSchemeNotePart(group: StoichiometryGroup): NotePartWithItem<NoteLinkedFile> | null {
    const notePart = group.containedNoteParts.find(
      (part) => isNotePartWithItem(part) && isNoteLinkedFile(part.item)
    );
    return (notePart as NotePartWithItem<NoteLinkedFile>) ?? null;
  }

  getStoichiometryTableNotePart(group: StoichiometryGroup): NotePartWithItem<StoichiometryStructuredTable> {
    const notePart = group.containedNoteParts.find(
      (part) => isNotePartWithItem(part) && isStructuredTable(part.item)
    );
    assert(notePart != null, 'Stoichiometry group must contain stoichiometry StructuredTable');
    return notePart as NotePartWithItem<StoichiometryStructuredTable>;
  }

  override supportsCopyPaste(): boolean {
    return true;
  }

  override requiresTableEvaluationOnInsert(): boolean {
    return true;
  }

  override supportsEntryLevelEvaluation(): boolean {
    return true;
  }

  getGroupSchemas(_group: StoichiometryGroup): Array<Schema> {
    return [];
  }

  getReactionStructuredTableNotePart(
    group: StoichiometryGroup
  ): NotePartWithItem<StoichiometryStructuredTable> {
    const reactionTableNotePart = group.containedNoteParts.find(
      (part) =>
        part.item && isStructuredTable(part.item) && part.item.kind === StructuredTableKind.STOICHIOMETRY
    );
    assert(
      reactionTableNotePart != null,
      'Reaction Structured Table must exist inside Reaction Note Linked Group'
    );
    return reactionTableNotePart as NotePartWithItem<StoichiometryStructuredTable>;
  }

  getReactionStructuredTable(group: StoichiometryGroup): StoichiometryStructuredTable {
    return this.getReactionStructuredTableNotePart(group).item;
  }
}
